import { RoccoLink } from '@rocco/components/link';
import { cn } from '@rocco/utils/cn';
import {
    Carousel,
    CarouselContent,
    CarouselItem,
} from '@rocco/components/carousel';
import { DIRECTORY_GUTTER_X } from './EditorialArticleGrid';
import { MagazineSeriesLabels } from '@modules/magazine/configs/constants';
import { MagazineCover } from '@modules/magazine/component/magazine-card/MagazineCover';
import { ReactComponent as CloseIcon } from '@rocco/icons/svg/close.svg';
import { EditorialCarouselPagination } from './EditorialCarouselPagination';
import type { MagazineDirectoryTileViewModel } from '~/modules/magazine/type';

export type MagazineEdition = MagazineDirectoryTileViewModel;

interface MagazineEditionCarouselProps {
    editions: MagazineEdition[];
    className?: string;
}

/**
 * The digital magazine edition carousel.
 *
 * Figma 49:4608: 446-wide cover cards on a 12px gap, each carrying its series
 * name and publication date beneath. The gutter sits on the carousel viewport
 * so the first card aligns to the page margin while the track runs on through
 * it, the same treatment as the featured carousel.
 *
 * Covers render through the magazines directory's own MagazineCover, so a
 * framed issue is composed rather than cropped.
 */
export const MagazineEditionCarousel = ({
    editions,
    className,
}: MagazineEditionCarouselProps) => {
    if (editions.length === 0) return null;

    return (
        <section
            aria-label="Digital magazines"
            className={cn('w-full', className)}
        >
            <Carousel
                options={{ align: 'start' }}
                ariaLabel="Latest magazine editions"
            >
                <CarouselContent
                    viewportClassName={DIRECTORY_GUTTER_X}
                    className="ml-0 gap-[0.75rem]"
                >
                    {editions.map((edition) => (
                        <CarouselItem
                            key={edition.id}
                            className="basis-[27.875rem] pl-0"
                        >
                            <RoccoLink
                                to={edition.link}
                                prefetch="intent"
                                className="flex flex-col"
                            >
                                {/*
                                 * 434/724 is the ratio the magazines directory
                                 * renders at, and covers are authored to it.
                                 * The design draws a shorter 446x605.5 box,
                                 * which crops the artwork top and bottom -- so
                                 * the cover's own ratio wins over the frame's.
                                 * Square corners: the design's cover is a plain
                                 * rectangle.
                                 */}
                                <div className="aspect-[434/724] w-full overflow-hidden">
                                    {/* This rail sits thousands of pixels below
                                        the fold, and cover artwork is heavy:
                                        every one of them was fetched at full
                                        size before the reader had scrolled at
                                        all. */}
                                    <MagazineCover
                                        magazine={edition}
                                        loading="lazy"
                                    />
                                </div>

                                {/* The design sets the button right after the
                                    344 title column, not against the card's
                                    right edge. */}
                                <div className="mt-[1.6188rem] flex items-start gap-[0.625rem]">
                                    <div className="flex w-[21.5rem] flex-col">
                                        <p className="m-0 font-sans text-[1.375rem] leading-[1.3] font-normal text-[#181818]">
                                            {MagazineSeriesLabels[
                                                edition.type
                                            ] ?? edition.type}
                                        </p>
                                        {edition.date && (
                                            <p className="m-0 font-sans text-[1.25rem] leading-[1.875rem] font-normal text-[#707070]">
                                                {edition.date}
                                            </p>
                                        )}
                                    </div>

                                    {/*
                                     * The design builds this plus from a times
                                     * glyph turned -45deg, so the same icon
                                     * serves both marks. Decorative — the whole
                                     * card is the link.
                                     */}
                                    <span
                                        aria-hidden={true}
                                        // -8.01px: the design nudges the
                                        // button block left of the gap.
                                        className="-ml-[0.5006rem] flex size-[2.6077rem] shrink-0 -rotate-45 items-center justify-center rounded-[var(--radius-btn-m)] border-[1.738px] border-solid border-[#181818] bg-[#181818]"
                                    >
                                        <CloseIcon className="size-[1.4753rem] fill-white" />
                                    </span>
                                </div>
                            </RoccoLink>
                        </CarouselItem>
                    ))}
                </CarouselContent>

                <EditorialCarouselPagination
                    total={editions.length}
                    label="Digital magazines carousel"
                />
            </Carousel>
        </section>
    );
};
